import { FC, useCallback, useEffect, useMemo, useState } from "react";

import { FilterColumnItem } from "@vviinn/user-managment-client-ts";
import { CampaignFilterSortingEnum } from "@vviinn/user-managment-client-ts/dist/models/Campaign";

import { Sortable } from "./Sortable";
import { NamedSelect } from "../../../../../ui/layout/molecules/NamedSelect";
import { FilterRenderData, UpdateFiltersData } from "./types";
import { compareFilterData } from "./helpers/compareFilterData";
import { FilterFeatures } from "./FilterFeatures";
import { SlideOverWrapper } from "../../../../../ui/layout/organisms/SlideOver/SlideOverWrapper/SlideOverWrapper";
import { useSlideOverSearch } from "../../../../../ui/layout/organisms/SlideOver/hooks/useSlideOverSearch";
import { SearchBox } from "../../../../../components/Interaction/SearchBox/SearchBox";
import { sortFilters } from "./helpers/sortFilters";
import { UpdateCampaignBody } from "../../../types";
import { Closeable } from "../../../../../ui/layout/organisms/SlideOver/SlideOverWrapper/types";
import { InfoRow, MODAL_ROW_GROUP_STYLES } from "../../../../../ui/layout";
import { filterSelectItemLabel } from "../../../../helpers/filterSelectItemLabel";

type FiltersModalProps = Closeable &
  UpdateCampaignBody & {
    initFiltersSorting?: CampaignFilterSortingEnum;
    activeFiltersAmount: number;
    initInterfaceControls: FilterRenderData[];
    enabledColumns: string[];
    defaultFiltersColumnsOrder: string[];
  };

const filtersSortOptions: CampaignFilterSortingEnum[] = ["relevance", "manual"];

const tabs = ["edit", "sort"];

export const FiltersSlideOver: FC<
  React.PropsWithChildren<FiltersModalProps>
> = ({
  open,
  onClose,
  initFiltersSorting = "relevance",
  updateCampaignBody,
  activeFiltersAmount,
  initInterfaceControls,
  enabledColumns,
  defaultFiltersColumnsOrder,
}) => {
  const [filtersSortBy, setFiltersSortBy] = useState<
    CampaignFilterSortingEnum | undefined
  >(initFiltersSorting);
  const [filterData, setFilterData] = useState<FilterRenderData[]>(
    initInterfaceControls,
  );
  const [activeTabIndex, setActiveTabIndex] = useState(0);

  const { search, setSearch, visibleItems } = useSlideOverSearch(
    open,
    filterData,
    (f, search) => filterSelectItemLabel(f.targetFilter).toLowerCase().includes(search),
  );

  const initConfig = useCallback(() => {
    setFilterData(initInterfaceControls);
    setFiltersSortBy(initFiltersSorting);
  }, [initInterfaceControls, initFiltersSorting]);

  useEffect(() => {
    setFilterData((oldFilterData) =>
      sortFilters(
        oldFilterData,
        filtersSortBy === "manual"
          ? enabledColumns
          : defaultFiltersColumnsOrder,
      ),
    );
  }, [filtersSortBy, enabledColumns, defaultFiltersColumnsOrder]);

  const updateFilters =
    (targetFilter: string) => (updateData: UpdateFiltersData) => {
      setFilterData((oldFilterData) =>
        oldFilterData.map((oldFilter) =>
          oldFilter.targetFilter !== targetFilter
            ? oldFilter
            : ({
                ...oldFilter,
                activeFilter:
                  updateData === undefined
                    ? undefined
                    : { ...oldFilter.activeFilter, ...updateData },
              } as FilterRenderData),
        ),
      );
    };

  const isSameFiltersConfig = useMemo(() => {
    if (filtersSortBy !== initFiltersSorting) return false;
    if (filterData.length !== initInterfaceControls.length) return false;
    return filterData.every((filter, index) =>
      compareFilterData(filter, initInterfaceControls[index]),
    );
  }, [filterData, initInterfaceControls, filtersSortBy, initFiltersSorting]);

  const handleAccept = useCallback(async () => {
    const relevantFilters = filterData
      .map(({ activeFilter }) => activeFilter)
      .filter((activeFilter) => activeFilter && activeFilter.column.length);
    return await updateCampaignBody({
      enabledFilterColumns: relevantFilters as FilterColumnItem[],
      filterSorting: filtersSortBy,
    });
  }, [filterData, filtersSortBy, updateCampaignBody]);

  return (
    <SlideOverWrapper
      open={open}
      onClose={onClose}
      initConfig={initConfig}
      title={`Filters (${activeFiltersAmount})`}
      tabProps={{
        tabs,
        setActiveTabIndex,
        tabGroupReference: "filters",
        defaultIndex: 0,
      }}
      buttonProps={{ disabled: isSameFiltersConfig, onClick: handleAccept }}
    >
      {activeTabIndex === 0 ? (
        <>
          <SearchBox
            placeholder="Name"
            value={search}
            onChange={({ target }) => setSearch(target.value.toLowerCase())}
          />
          {visibleItems.map((item) => (
            <FilterFeatures
              {...item}
              key={item.columnGroupLabel ?? item.targetFilter}
              updateFilters={updateFilters(item.targetFilter)}
            />
          ))}
        </>
      ) : (
        <>
          <div className={MODAL_ROW_GROUP_STYLES}>
            <NamedSelect
              title="Sort by"
              onChange={(target) =>
                setFiltersSortBy(target.value as CampaignFilterSortingEnum)
              }
              value={filtersSortBy}
              options={filtersSortOptions}
            />
            {filtersSortBy === "manual" && (
              <InfoRow infoText="Only active filters can be ordered" />
            )}
          </div>
          <Sortable
            disabled={filtersSortBy !== "manual"}
            filterData={filterData}
            setFilterData={setFilterData}
          />
        </>
      )}
    </SlideOverWrapper>
  );
};
